import { useState } from "react";
import { ALL_USERS, C } from "../../../../constants";
import { daysUntil, getInit, relTime, uid } from "../../../../utils";

function AvatarBadge({ initials, size = 26 }) {
  return (
    <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.34 }}>
      {initials}
    </div>
  );
}

function MentionInput({ value, onChange, placeholder, rows = 3, onSubmit, className = "" }) {
  const [mentionQ, setMentionQuery] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const filtered = mentionQ !== null ? ALL_USERS.filter((user) => user.toLowerCase().includes(mentionQ.toLowerCase())) : [];

  function handleChange(event) {
    const text = event.target.value;
    onChange(text);
    const cursor = event.target.selectionStart;
    const before = text.slice(0, cursor);
    const match = before.match(/@([\w ]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setSelectedIndex(0);
    } else {
      setMentionQuery(null);
    }
  }

  function insertMention(user) {
    const target = document.activeElement;
    const cursor = target?.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const start = before.lastIndexOf("@");
    const nextValue = `${before.slice(0, start)}@${user} ${after}`;
    onChange(nextValue);
    setMentionQuery(null);
  }

  function handleKey(event) {
    if (mentionQ !== null && filtered.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((index) => Math.min(index + 1, filtered.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((index) => Math.max(index - 1, 0));
      } else if (event.key === "Enter") {
        event.preventDefault();
        insertMention(filtered[selectedIndex]);
        return;
      } else if (event.key === "Escape") {
        setMentionQuery(null);
      }
    }

    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && onSubmit) {
      onSubmit();
    }
  }

  return (
    <div className="mention-wrap">
      {mentionQ !== null && filtered.length > 0 && (
        <div className="mention-dd">
          <div className="mention-hint">Mention a teammate</div>
          {filtered.map((user, index) => (
            <div key={user} className={`mention-item${index === selectedIndex ? " msel" : ""}`} onMouseDown={(event) => { event.preventDefault(); insertMention(user); }}>
              <AvatarBadge initials={getInit(user)} size={22} />
              <span>{user}</span>
            </div>
          ))}
        </div>
      )}
      <textarea className={`mention-ta${className ? ` ${className}` : ""}`} rows={rows} placeholder={placeholder} value={value} onChange={handleChange} onKeyDown={handleKey} />
    </div>
  );
}

function parseMentions(text) {
  if (!text) return [];
  return [...new Set((text.match(/@([\w ]+)/g) || []).map((token) => token.slice(1).trim()).filter(Boolean))];
}

function RenderMentions({ text }) {
  const parts = text.split(/(@[\w ]+)/g);
  return <>{parts.map((part, index) => /^@/.test(part) ? <strong key={index} className="mention-highlight">{part}</strong> : part)}</>;
}

function AIEmailModal({ report, projects, onClose, onSend, currentUser }) {
  const [phase, setPhase] = useState("idle");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [stepIdx, setStepIdx] = useState(0);

  const steps = [
    "Parsing report data...",
    "Analysing project statuses...",
    "Identifying key risks...",
    "Composing executive summary...",
    "Drafting project highlights...",
    "Finalising email...",
  ];

  function buildPrompt() {
    const projectLines = report.projectRows.map((row) => {
      const project = projects.find((item) => item.projectId === row.projectId);
      const npsText = typeof row.NPS === "number" ? row.NPS : "N/A";
      return `- ${project?.projectName || row.projectId} (${project?.clientName}): ${row.status} | NPS ${npsText} | Util ${row.utilisation}% | ${row.note}`;
    }).join("\n");

    return [
      "You are a senior delivery director at a tech consultancy called Raincode. Write a professional weekly project status email to internal leadership.",
      "",
      `REPORT: ${report.weekLabel}`,
      "",
      "SUMMARY METRICS:",
      `• Average team utilisation: ${report.summary.avgUtilisation}%`,
      `• Total billables: ${(report.summary.totalBillables / 1000).toFixed(0)}K kr`,
      `• Active blockers: ${report.summary.openBlockers}`,
      `• Overdue actions: ${report.summary.overdueActions}`,
      `• Critical projects: ${report.summary.criticalProjects}`,
      "",
      "PROJECT STATUS (per client):",
      projectLines,
      "",
      "KEY INSIGHTS:",
      report.insights.map((insight) => `• ${insight}`).join("\n"),
      "",
      'Write a professional, concise weekly status email. Structure:',
      'Subject: [professional subject line, prefix with "Subject: "]',
      "",
      "Then body with: 1) Executive Summary (2-3 sentences), 2) Project Highlights (one line per project), 3) Risks & Escalations, 4) Actions This Week (3-5 bullets), 5) Closing.",
      "Professional, direct tone. No placeholders. No markdown headers in body.",
    ].join("\n");
  }

  async function generate() {
    setPhase("loading");
    setStepIdx(0);
    const interval = setInterval(() => setStepIdx((index) => Math.min(index + 1, steps.length - 1)), 600);

    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: [{ role: "user", content: buildPrompt() }] }),
      });
      const data = await response.json();
      clearInterval(interval);
      const text = (data.content || []).filter((block) => block.type === "text").map((block) => block.text).join("\n");
      const subjectMatch = text.match(/Subject:\s*(.+)/);
      setSubject(subjectMatch ? subjectMatch[1].trim() : `Weekly Project Status — ${report.weekLabel}`);
      setBody(text.replace(/^Subject:.+\n?/m, "").trim());
      setPhase("ready");
    } catch {
      clearInterval(interval);
      setSubject(`Weekly Project Status — ${report.weekLabel}`);
      setBody([
        "Hi team,",
        "",
        `Weekly project status summary for ${report.weekLabel}.`,
        "",
        "EXECUTIVE SUMMARY",
        `Average utilisation this week is ${report.summary.avgUtilisation}% with ${(report.summary.totalBillables / 1000).toFixed(0)}K kr in active billables. We have ${report.summary.openBlockers} active blockers${report.summary.criticalProjects > 0 ? ` and ${report.summary.criticalProjects} critical project(s) requiring escalation` : ""}.`,
        "",
        "PROJECT HIGHLIGHTS",
        ...report.projectRows.map((row) => {
          const project = projects.find((item) => item.projectId === row.projectId);
          return `• ${project?.clientName} — ${project?.projectName}: ${row.status}. ${row.note}`;
        }),
        "",
        "RISKS & ESCALATIONS",
        ...report.insights.filter((insight) => insight.includes("🔴") || insight.includes("⚠")).map((insight) => `• ${insight.replace(/^[🔴⚠✅📋]\s*/, "")}`),
        "",
        "ACTIONS THIS WEEK",
        "• Resolve all outstanding blockers — prioritise Halcyon Insurance",
        "• Chase bandwidth resolution — Nordveld Energy",
        "• Confirm renewal discussions — Celsius Group",
        "• Review SOW amendment status — Arcturus Bank",
        "",
        "Best regards,",
        currentUser.name,
      ].join("\n"));
      setPhase("ready");
    }
  }

  return (
    <div className="modal-bg" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="ai-modal">
        <div className="ai-modal-head">
          <div>
            <div className="ai-eyebrow"><span>✦</span> AI-Assisted Drafting</div>
            <div className="ai-modal-title">Generate Weekly Report Email</div>
            <div className="ai-modal-sub">{report.weekLabel}</div>
          </div>
          <button className="ai-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ai-modal-body">
          {phase === "idle" && (
            <div className="ai-idle">
              <span className="ai-idle-icon">✦</span>
              <p className="ai-idle-desc">Claude will analyse this week&apos;s project data, NPS scores, utilisation figures, and blockers, then draft a professional stakeholder email ready to review and send.</p>
              <div className="ai-data-preview">
                <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>Data inputs</div>
                {[["Week", report.weekLabel], ["Projects", report.projectRows.length], ["Avg Util", `${report.summary.avgUtilisation}%`], ["Billables", `${(report.summary.totalBillables / 1000).toFixed(0)}K kr`], ["Blockers", report.summary.openBlockers], ["Insights", report.insights.length]].map(([key, value]) => (
                  <div className="ai-data-row" key={key}><span className="ai-data-key">{key}</span><span className="ai-data-val">{value}</span></div>
                ))}
              </div>
              <button className="wr-ai-btn" onClick={generate} style={{ fontSize: 14, padding: "11px 24px" }}><span>✦</span> Generate Email Draft</button>
            </div>
          )}
          {phase === "loading" && (
            <div className="ai-loading">
              <div className="ai-spinner" />
              <div className="ai-loading-title">Analysing report data…</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 14 }}>
                {steps.map((step, index) => <div key={index} className={`ai-step${index === stepIdx ? " active" : index < stepIdx ? " done" : ""}`}><span className="ai-step-ico">{index < stepIdx ? "✓" : index === stepIdx ? "›" : "·"}</span>{step}</div>)}
              </div>
            </div>
          )}
          {phase === "ready" && (
            <div className="ai-result">
              <div>
                <div className="ai-field-lbl">Subject line</div>
                <input className="ai-subject-input" value={subject} onChange={(event) => setSubject(event.target.value)} />
              </div>
              <div>
                <div className="ai-field-lbl">Email body — review and edit before sending</div>
                <textarea className="ai-body-ta" value={body} onChange={(event) => setBody(event.target.value)} />
              </div>
              <div className="ai-disclaimer"><span style={{ color: "#a855f7" }}>✦</span> AI-generated — verify all facts before sending</div>
            </div>
          )}
        </div>
        {phase !== "idle" && (
          <div className="ai-modal-foot">
            {phase === "ready" ? (
              <>
                <button className="btn-ai-send" onClick={() => onSend(subject, body)}>📤 Send Email</button>
                <button className="btn-regen" onClick={() => setPhase("idle")}>↺ Regenerate</button>
                <button className="btn-sec" style={{ padding: "9px 16px", fontSize: 13 }} onClick={onClose}>Cancel</button>
                <span className="ai-disclaimer" style={{ marginLeft: "auto" }}><span style={{ color: "#a855f7" }}>✦</span> Powered by Claude</span>
              </>
            ) : (
              <button className="btn-sec" style={{ padding: "9px 16px", fontSize: 13 }} onClick={onClose}>Cancel</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DevWeeklyReports({ projects }) {
  const [expandedProjects, setExpandedProjects] = useState(() => {
    const map = {};
    projects.forEach((project) => {
      if (project.devReports && project.devReports.length > 0) map[project.projectId] = true;
    });
    return map;
  });
  const [expandedReports, setExpandedReports] = useState({});

  const moodColor = { green: C.green, yellow: C.yellow, red: C.red };
  const moodLabel = { green: "On Track", yellow: "Watch", red: "At Risk" };
  const projectsWithReports = projects.filter((project) => project.devReports && project.devReports.length > 0);
  const totalReports = projectsWithReports.reduce((sum, project) => sum + project.devReports.length, 0);

  return (
    <div className="wr-section">
      <div className="wr-section-head">
        <span className="wr-section-title">Developer Weekly Reports</span>
        <span style={{ fontSize: 11, color: C.dim }}>{totalReports} reports across {projectsWithReports.length} projects</span>
      </div>
      <div className="wr-section-body">
        {projectsWithReports.length === 0 ? (
          <div style={{ color: C.dim, fontSize: 13 }}>No projects with developer reports.</div>
        ) : (
          projectsWithReports.map((project) => {
            const isOpen = expandedProjects[project.projectId];
            const reports = project.devReports || [];
            const latestMood = reports[reports.length - 1]?.mood || "green";
            return (
              <div key={project.projectId} className="dwr-project-group">
                <div className="dwr-project-header" onClick={() => setExpandedProjects((state) => ({ ...state, [project.projectId]: !state[project.projectId] }))}>
                  <span className={`dwr-chevron${isOpen ? " open" : ""}`}>▶</span>
                  <span className="dwr-project-name">{project.projectName}</span>
                  <span className="dwr-project-client">{project.clientName}</span>
                  <span className="dwr-mood" style={{ background: moodColor[latestMood] || C.dim }} title={moodLabel[latestMood] || ""} />
                  <span className="dwr-report-count">{reports.length} report{reports.length !== 1 ? "s" : ""}</span>
                </div>
                {isOpen && (
                  <div className="dwr-reports-list">
                    {reports.slice().reverse().map((report) => {
                      const reportOpen = expandedReports[report.id];
                      return (
                        <div key={report.id} className="dwr-report-card">
                          <div className="dwr-report-top" onClick={() => setExpandedReports((state) => ({ ...state, [report.id]: !state[report.id] }))}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                              <span className="dwr-mood" style={{ background: moodColor[report.mood] || C.dim }} />
                              <span className="dwr-report-week">{report.week}</span>
                              <span className="dwr-report-date">{report.dateRange || report.date}</span>
                            </div>
                            <div className="dwr-report-author">
                              <AvatarBadge initials={getInit(report.author)} size={20} />
                              <span>{report.author}</span>
                              <span style={{ fontSize: 10, color: C.dim, marginLeft: 4 }}>{reportOpen ? "▾" : "▸"}</span>
                            </div>
                          </div>
                          {reportOpen && (
                            <div className="dwr-report-body">
                              <div className="dwr-meta-row">
                                <span className="dwr-meta-item"><strong>Consultant:</strong>{report.consultant || report.author}</span>
                                <span className="dwr-meta-item"><strong>Client:</strong>{report.client || project.clientName}</span>
                                <span className="dwr-meta-item"><strong>Sales Rep:</strong>{report.salesRep || "—"}</span>
                                <span className="dwr-meta-item"><strong>Project:</strong>{project.projectName}</span>
                              </div>
                              <div className="dwr-summary">{report.summary}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function NoteComposer({ onPost, currentUser }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("comment");

  function post() {
    if (!text.trim()) return;
    const mentions = parseMentions(text);
    onPost({
      id: uid(),
      author: currentUser.name,
      initials: currentUser.initials,
      text: text.trim(),
      ts: "2026-03-24",
      type,
      mentions,
    });
    setText("");
  }

  return (
    <div>
      <MentionInput value={text} onChange={setText} placeholder="Add a team note… type @name to mention a colleague" rows={3} onSubmit={post} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 7 }}>
        <div style={{ display: "flex", gap: 4 }}>
          {["comment", "update"].map((kind) => <button key={kind} className={`ctype-btn${type === kind ? " sel" : ""}`} onClick={() => setType(kind)}>{kind.charAt(0).toUpperCase() + kind.slice(1)}</button>)}
        </div>
        <button className="post-btn" onClick={post} disabled={!text.trim()}>Post ⌘↵</button>
      </div>
    </div>
  );
}

export default function WeeklyReportsPage({ weeklyReports, setWeeklyReports, projects, tasks, addNotification, showToast, currentUser }) {
  const [selectedId, setSelectedId] = useState(weeklyReports[0]?.reportId);
  const [showAI, setShowAI] = useState(false);
  const [generating, setGenerating] = useState(false);

  const report = weeklyReports.find((item) => item.reportId === selectedId) || weeklyReports[0];

  function autoGenerate() {
    setGenerating(true);
    setTimeout(() => {
      const avgUtil = Math.round(projects.reduce((sum, project) => sum + project.utilizationPct, 0) / projects.length);
      const totalBill = projects.reduce((sum, project) => sum + project.billables, 0);
      const openTasks = tasks.filter((task) => task.status !== "Done" && task.status !== "Cancelled");
      const blockers = openTasks.filter((task) => task.status === "Blocked").length;
      const overdue = openTasks.filter((task) => daysUntil(task.dueDate) < 0).length;
      const newReport = {
        reportId: `wr_${uid()}`,
        weekLabel: "Week 13 — 25–31 Mar 2026",
        weekStart: "2026-03-25",
        weekEnd: "2026-03-31",
        generatedAt: "2026-03-25T09:00:00",
        status: "current",
        summary: {
          avgUtilisation: avgUtil,
          totalBillables: totalBill,
          openBlockers: blockers,
          overdueActions: overdue,
          criticalProjects: projects.filter((project) => project.projectStatus === "Critical").length,
          atRiskProjects: projects.filter((project) => project.projectStatus === "At Risk").length,
        },
        projectRows: projects.map((project) => ({
          projectId: project.projectId,
          status: project.projectStatus,
          // Effective NPS (project override → client fallback). 0 is
          // treated as "not set" so rows with pre-Phase-B default values
          // inherit the client NPS instead of showing a red "0".
          NPS: (typeof project.NPS === "number" && project.NPS > 0
            ? project.NPS
            : (typeof project.clientNps === "number" && project.clientNps > 0 ? project.clientNps : null)),
          utilisation: project.utilizationPct,
          note: project.riskNotes || "Stable — no issues this week",
        })),
        insights: [
          `${avgUtil >= 85 ? "✅" : "⚠ "} Team utilisation at ${avgUtil}% — ${avgUtil >= 85 ? "above" : "below"} 85% target`,
          `📋 ${blockers} blocked tasks require resolution this week`,
          `📋 ${overdue} overdue actions across active projects`,
        ],
        teamNotes: [],
      };
      setWeeklyReports((current) => [newReport, ...current]);
      setSelectedId(newReport.reportId);
      setGenerating(false);
      showToast("Report generated", "Week 13 report is ready to review");
    }, 1400);
  }

  function postNote(comment) {
    setWeeklyReports((current) => current.map((item) => item.reportId === report.reportId ? { ...item, teamNotes: [...(item.teamNotes || []), comment] } : item));
    comment.mentions.forEach((mention) => {
      if (mention.toLowerCase() === currentUser.name.toLowerCase()) {
        addNotification({ id: uid(), type: "mention", text: `You were @mentioned in the ${report.weekLabel} weekly report`, ts: new Date().toISOString(), read: false });
      }
    });
  }

  function handleSend(subject) {
    showToast("Email sent", `"${subject.slice(0, 40)}…" delivered to stakeholders`);
    setShowAI(false);
  }

  if (!report) return <div style={{ padding: 32, color: C.sub }}>No reports available.</div>;

  const statusStyle = { "On Track": "#10c981", "At Risk": "#f59e0b", Critical: "#f04444", Pending: "#364f72" };
  const statusClass = { "On Track": "st-ontrack", "At Risk": "st-atrisk", Critical: "st-critical", Pending: "st-pending" };

  return (
    <div className="fade">
      <div className="pg-top">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="pg-title">Weekly Reports</div>
            <div className="pg-sub">Auto-generated · versioned · AI email drafting · @mention teammates</div>
          </div>
          <div style={{ display: "flex", gap: 8, paddingTop: 2 }}>
            <button className="wr-gen-btn" onClick={autoGenerate} disabled={generating}>{generating ? "⟳ Generating…" : "⟳ Generate This Week"}</button>
            <button className="wr-ai-btn" onClick={() => setShowAI(true)}><span>✦</span> Draft AI Email</button>
          </div>
        </div>
      </div>
      <div className="pg-body">
        <div className="wr-layout">
          <div className="wr-sidebar">
            <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".12em", marginBottom: 5, paddingLeft: 2 }}>Report History</div>
            {weeklyReports.map((item) => (
              <button key={item.reportId} className={`wr-week-btn${item.reportId === selectedId ? " active" : ""}`} onClick={() => setSelectedId(item.reportId)}>
                <div className="wr-week-label">{item.weekLabel}</div>
                <div className="wr-week-date">{item.weekStart} → {item.weekEnd}</div>
                <div className="wr-status-badge" style={{ color: item.status === "current" ? C.green : C.dim }}>{item.status === "current" ? "● Current" : "○ Archived"}</div>
              </button>
            ))}
          </div>
          <div className="wr-content">
            <div className="wr-section">
              <div className="wr-section-head">
                <span className="wr-section-title">Week Summary — {report.weekLabel}</span>
                <span style={{ fontSize: 11, color: C.dim }}>Generated {relTime(report.generatedAt)}</span>
              </div>
              <div className="wr-section-body">
                <div className="wr-kpi-grid">
                  {[
                    { label: "Avg Utilisation", value: `${report.summary.avgUtilisation}%`, color: report.summary.avgUtilisation >= 85 ? C.green : report.summary.avgUtilisation >= 70 ? C.yellow : C.red },
                    { label: "Total Billables", value: `${(report.summary.totalBillables / 1000).toFixed(0)}K kr`, color: C.blue },
                    { label: "Active Blockers", value: report.summary.openBlockers, color: report.summary.openBlockers > 3 ? C.red : report.summary.openBlockers > 0 ? C.yellow : C.green },
                    { label: "Overdue Actions", value: report.summary.overdueActions, color: report.summary.overdueActions > 0 ? C.yellow : C.green },
                    { label: "Critical Projects", value: report.summary.criticalProjects, color: report.summary.criticalProjects > 0 ? C.red : C.green },
                    { label: "At-Risk Projects", value: report.summary.atRiskProjects, color: report.summary.atRiskProjects > 1 ? C.yellow : C.green },
                  ].map(({ label, value, color }) => (
                    <div className="wr-kpi" key={label}>
                      <div className="wr-kpi-lbl">{label}</div>
                      <div className="wr-kpi-val" style={{ color }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="wr-section">
              <div className="wr-section-head"><span className="wr-section-title">Key Insights</span></div>
              <div className="wr-section-body">
                {report.insights.map((insight, index) => {
                  const isCrit = insight.includes("🔴");
                  const isWarn = insight.includes("⚠");
                  const isGood = insight.includes("✅");
                  return <div key={index} className={`wr-insight${isCrit ? " crit" : isWarn ? " warn" : isGood ? " good" : ""}`}>{insight}</div>;
                })}
              </div>
            </div>
            <div className="wr-section">
              <div className="wr-section-head"><span className="wr-section-title">Project Status per Client</span></div>
              <div className="wr-section-body">
                {report.projectRows.map((row) => {
                  const project = projects.find((item) => item.projectId === row.projectId);
                  if (!project) return null;
                  return (
                    <div className="wr-proj-row" key={row.projectId}>
                      <div style={{ flex: 1.2 }}>
                        <div className="wr-proj-name">{project.projectName}</div>
                        <div className="wr-proj-client">{project.clientName}</div>
                      </div>
                      <span className={`chip ${statusClass[row.status] || "st-pending"}`} style={{ flexShrink: 0 }}>
                        <span className="dot" style={{ background: statusStyle[row.status] || C.dim }} />{row.status}
                      </span>
                      <span className="wr-proj-note">{row.note}</span>
                      <span className="wr-proj-nps" style={{ color: typeof row.NPS === "number" ? (row.NPS >= 4 ? C.green : row.NPS >= 3 ? C.yellow : C.red) : C.dim }}>{row.NPS ?? "—"}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div className="wr-util-bar"><div className="wr-util-fill" style={{ width: `${row.utilisation}%`, background: row.utilisation >= 80 ? C.green : row.utilisation >= 60 ? C.yellow : C.red }} /></div>
                        <span className="mono" style={{ fontSize: 11, color: C.dim, minWidth: 28 }}>{row.utilisation}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="wr-section">
              <div className="wr-section-head">
                <span className="wr-section-title">Active Blockers &amp; Overdue Actions</span>
                <span style={{ fontSize: 11, color: C.dim }}>{report.summary.openBlockers + report.summary.overdueActions} items</span>
              </div>
              <div className="wr-section-body">
                {report.projectRows.filter((row) => row.status === "Critical" || row.status === "At Risk").map((row) => {
                  const project = projects.find((item) => item.projectId === row.projectId);
                  const isCritical = row.status === "Critical";
                  return (
                    <div key={row.projectId} className={`wr-blocker${isCritical ? "" : " warn"}`}>
                      <div className="wr-blocker-dot" style={{ background: isCritical ? C.red : C.yellow }} />
                      <div>
                        <div className="wr-blocker-title">{project?.projectName} — {row.status}</div>
                        <div className="wr-blocker-meta">{project?.clientName} · {row.note}</div>
                      </div>
                    </div>
                  );
                })}
                {report.projectRows.filter((row) => row.status === "Critical" || row.status === "At Risk").length === 0 && <div style={{ color: C.dim, fontSize: 13 }}>No blockers or critical items this week. ✓</div>}
              </div>
            </div>
            <DevWeeklyReports projects={projects} />
            <div className="wr-section">
              <div className="wr-section-head">
                <span className="wr-section-title">Team Notes</span>
                <span style={{ fontSize: 11, color: C.dim }}>{(report.teamNotes || []).length} notes</span>
              </div>
              <div className="wr-section-body">
                {(report.teamNotes || []).length === 0 && <div style={{ color: C.dim, fontSize: 12, marginBottom: 12 }}>No notes yet — add a note below.</div>}
                {(report.teamNotes || []).map((comment) => (
                  <div key={comment.id} style={{ display: "flex", gap: 9, padding: "8px 0", borderBottom: "1px solid #0a1228" }}>
                    <AvatarBadge initials={comment.initials} size={26} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{comment.author}</span>
                        {comment.type === "update" && <span className="cmt-upd">Update</span>}
                        <span style={{ fontSize: 10.5, color: C.dim }}>{relTime(comment.ts)}</span>
                        {(comment.mentions || []).map((mention) => <span key={mention} className="mention-badge"><AvatarBadge initials={getInit(mention)} size={12} />@{mention}</span>)}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#8fa4c4", lineHeight: 1.6 }}><RenderMentions text={comment.text} /></div>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 12 }}>
                  <NoteComposer onPost={postNote} currentUser={currentUser} />
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.dim }}>Generated: {report.generatedAt} · {report.status}</div>
          </div>
        </div>
      </div>
      {showAI && <AIEmailModal report={report} projects={projects} onClose={() => setShowAI(false)} onSend={handleSend} currentUser={currentUser} />}
    </div>
  );
}
